import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiEye,
  FiXCircle,
  FiSend,
  FiClock,
  FiCheckCircle,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";
import { Inbox } from "lucide-react";
import {
  listMyProposals,
  withdrawProposal,
  typeLabel,
  formatPrice,
  formatDate,
} from "../../utils/missionsApi";
import MissionTypeIcon from "./components/MissionTypeIcon";

const STATUS_META = {
  envoyee:  { label: "Envoyée",         tone: "info",     icon: <FiSend /> },
  vue:      { label: "Vue par le client", tone: "info",   icon: <FiEye /> },
  acceptee: { label: "Acceptée",        tone: "success",  icon: <FiCheckCircle /> },
  refusee:  { label: "Refusée",         tone: "danger",   icon: <FiXCircle /> },
  retiree:  { label: "Retirée",         tone: "neutral",  icon: <FiXCircle /> },
  expiree:  { label: "Expirée",         tone: "neutral",  icon: <FiClock /> },
};

export default function MyApplications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items: list } = await listMyProposals();
      setItems(list || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleWithdraw = async (id) => {
    if (!window.confirm("Retirer cette candidature ?")) return;
    try {
      await withdrawProposal(id);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur lors du retrait");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="pmm-topbar">
        <div>
          <h1 className="pmm-h-accent">Mes candidatures</h1>
          <div className="pmm-topbar-sub">
            Toutes les missions pour lesquelles vous avez envoyé une proposition.
          </div>
        </div>
      </div>

      {loading ? <div className="pmm-spinner" />
        : items.length === 0 ? (
          <div className="pmm-empty">
            <div className="pmm-empty-icon"><Inbox size={28} /></div>
            <h3>Aucune candidature</h3>
            <p>Postulez à votre première mission depuis la liste « Missions disponibles ».</p>
            <div style={{ marginTop: 12 }}>
              <button className="pmm-btn pmm-btn--primary" onClick={() => navigate("/missions/list")}>
                Voir les missions disponibles
              </button>
            </div>
          </div>
        ) : (
          <div className="pmm-grid pmm-grid--2">
            {items.map((p) => {
              const m = p.missionId || {};
              const meta = STATUS_META[p.status] || STATUS_META.envoyee;
              return (
                <article className="pmm-mission-card" key={p._id}>
                  <div className="pmm-mission-card__head">
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                      <MissionTypeIcon type={m.type} size={38} />
                      <div style={{ minWidth: 0 }}>
                        <h3 className="pmm-mission-card__title">
                          {m.title || "Mission supprimée"}
                        </h3>
                        <div className="pmm-mission-card__type">{typeLabel(m.type)}</div>
                      </div>
                    </div>
                    <span className={`pmm-chip pmm-chip--${meta.tone}`}>
                      {meta.icon} {meta.label}
                    </span>
                  </div>

                  <div className="pmm-mission-card__meta">
                    <span><FiMapPin /> <b>{m.city || "—"}</b></span>
                    <span><FiCalendar /> {formatDate(m.date)}</span>
                    <span><FiSend /> Mon prix : <b>{formatPrice(p.price)}</b></span>
                    <span><FiClock /> {p.estimatedDelay || "—"}</span>
                  </div>

                  <div className="pmm-mission-card__actions">
                    {m._id && (
                      <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate(`/missions/${m._id}`)}>
                        <FiEye /> Voir la mission
                      </button>
                    )}
                    {["envoyee", "vue"].includes(p.status) && (
                      <button className="pmm-btn pmm-btn--danger" onClick={() => handleWithdraw(p._id)}>
                        <FiXCircle /> Retirer
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
    </div>
  );
}
